import {
  Component,
  Event,
  EventEmitter,
  forceUpdate,
  getAssetPath,
  h,
  Host,
  State,
} from '@stencil/core';
import { Element, Listen } from '@stencil/core/internal';

import wcShared from '@web-components/shared';

import helpdeskCommon from '../../../../utils/HelpdeskCommon.class';
import { MessagesStore } from '../../../../utils/Stores/messages-store';
import { Store } from '../../../../utils/Stores/store';

@Component({
  tag: 'bot-component',
  styleUrl: 'bot-module.scss',
  shadow: true,
})
export class ModuleBot {
  private messageHTML: HTMLElement;

  @Element() el: HTMLBotComponentElement;

  @State() messageUser: string;
  @State() loadingUser = false;
  @State() loadingSending = false;
  @State() messages: IWCChatMessage[] = [];
  @State() realoadAppStatus = false;
  @State() loadingReset = false;

  @Event() sectionChangeEvent: EventEmitter<IWCContextClose[]>;

  private scrollDown(timer: number) {
    setTimeout(() => {
      const allMessages = document
        ?.querySelector('module-helpdesk-component')
        ?.shadowRoot?.querySelector('main-helpdesk-switch-component')
        ?.shadowRoot?.querySelector('bot-component')
        ?.shadowRoot?.querySelector('bot-messages-component')
        ?.shadowRoot?.querySelectorAll('bot-message-component');
      const last: HTMLBotMessageComponentElement = allMessages?.[allMessages.length - 1];
      const posY = last?.offsetTop || 0;
      const height = last?.offsetHeight || 0;
      const result = height + posY;

      wcShared?.store?.utils?.smoothScrollTo(this.messageHTML, result, timer);
    }, 300);
  }

  private sendMessage(message: string, type = 'text') {
    if ((typeof message === 'string' && message?.trim() === '') || message === undefined) {
      return;
    }

    this.loadingSending = true;
    wcShared?.store?.loaderStart('loadingSending');

    // Create a message of user to push it in conversation
    const userMessage = {
      title: message,
      action: null,
      type,
      sender_type: 'customer',
      created_at: Date.now(),
      messages: [],
      disabled_input: true,
    } as IWCChatMessage;

    // Create an empty message from the bot to generate a loader in conversation
    const botMessage = {
      title: null,
      action: null,
      type: 'loading',
      sender_type: 'bot',
      created_at: Date.now(),
      messages: [],
      disabled_input: true,
    } as IWCChatMessage;

    // Add messages at the conversation
    MessagesStore?.set('messages', [...MessagesStore?.get('messages'), userMessage, botMessage]);

    this.messageUser = '';
    let url = `/api/components/chatbot/conversation?lang=${wcShared?.store?.getLang()}&timezone=${
      Boolean(Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone)
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'Europe/Zurich'
    }`;
    url += helpdeskCommon.urlFormatFaq();

    wcShared
      ?.httpCall('POST', url, {
        message,
      })
      .then((r) => {
        wcShared?.store?.loaderStop('loadingSending').then(() => (this.loadingSending = false));
        if (r.status === 200) {
          return r.json().then((response) => {
            Store?.set('bot_id', response.data.bot_id);
            Store?.set('channel_id', response.data.channel_id);

            response.data.messages?.map((message) => {
              MessagesStore?.set('messages', [
                ...MessagesStore?.get('messages'),
                { ...message, created_at: Date.now() },
              ]);
              MessagesStore?.set(
                'messages',
                MessagesStore?.get('messages').filter((item) => item.type !== 'loading'),
              );
            });
            forceUpdate(this);
          });
        } else {
          this.sectionChangeEvent.emit(['error_default']);
        }
      })
      .catch(() => {
        wcShared?.store?.loaderStop('loadingSending').then(() => (this.loadingSending = false));
      });
  }

  // Get text of user from the chatbot
  private handleInputText(event) {
    this.messageUser = event.target.value;
  }

  // Shortcut to sending message
  private keyPress(e) {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage(this.messageUser);
    }
  }

  // Get information that messages has been load on component. Trigger a scrolldown of chat view
  @Listen('messagesEnded')
  messagesEndedHandler(event: CustomEvent<number>) {
    setTimeout(() => {
      this.scrollDown(event.detail);
    });
  }

  // Get the value from Select. Send a message to the bot with the selected value
  @Listen('selectValue')
  selectValueHandler(event: CustomEvent<{ value: string; type: string }>) {
    this.sendMessage(event.detail.value, event.detail.type);
  }

  // DEBUG FUNCTION
  // private displayDebug() {
  //   alert(
  //     wcShared?.translate('Merci de nous communiquer les informations suivantes : ') +
  //       '\n\nChannel Id : ' +
  //       Store?.get('channel_id') +
  //       ' \nBot Id : ' +
  //       Store?.get('bot_id') +
  //       '\n\n' +
  //       wcShared.translate('Merci'),
  //   );
  // }

  render() {
    return (
      <Host>
        <div class="chat">
          {MessagesStore?.get('messages').length === 0 || this.loadingUser ? (
            <module-loader-component />
          ) : (
            <div class="full-height">
              <div
                class={{
                  'chat-messages': true,
                  'chat-messages--no-input':
                    MessagesStore?.get('messages')[MessagesStore?.get('messages')?.length - 1]
                      ?.disabled_input,
                  'chat-messages--full': Store?.get('is_full'),
                }}
                ref={(el) => {
                  this.messageHTML = el as HTMLElement;
                }}
              >
                <bot-messages-component
                  messages={MessagesStore?.get('messages')}
                ></bot-messages-component>
              </div>
              {!MessagesStore?.get('messages')[MessagesStore?.get('messages')?.length - 1]
                ?.disabled_input ? (
                <div class={{ 'chat-input': true }}>
                  <textarea
                    class={{
                      disabled:
                        MessagesStore?.get('messages')[MessagesStore?.get('messages')?.length - 1]
                          ?.disabled_input,
                    }}
                    value={this.messageUser}
                    disabled={
                      MessagesStore?.get('messages')[MessagesStore?.get('messages')?.length - 1]
                        ?.disabled_input
                    }
                    onKeyPress={(e) => {
                      this.keyPress(e);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      e.stopPropagation();
                    }}
                    onInput={(e) => this.handleInputText(e)}
                    autofocus="true"
                    placeholder={wcShared.translate('Saisissez votre message')}
                  ></textarea>
                  <div
                    class={{
                      'chat-input__actions': true,
                      disabled:
                        !Boolean(this.messageUser) ||
                        this.messageUser?.trim() === '' ||
                        MessagesStore?.get('messages')[MessagesStore?.get('messages')?.length - 1]
                          ?.disabled_input,
                    }}
                  >
                    <img
                      onClick={() => {
                        this.sendMessage(this.messageUser);
                      }}
                      src={getAssetPath('./../assets/icons/send.svg')}
                      alt={wcShared.translate('Envoyer')}
                    />
                  </div>
                </div>
              ) : (
                ''
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
